import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList, LogOut, Loader2, Plus, Search, ListChecks, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import api from "@/lib/api";
import { ORDER_STATUS_META, ORDER_STATUS_ORDER } from "@/lib/orderStatus";

function fmtDate(d) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}
const fmtPct = (n) => `${Number(n).toFixed(1).replace(".", ",")}%`;

export default function OrdersList() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const fetchOrders = useCallback(async (currentStatus, currentQ) => {
    setLoading(true);
    try {
      const params = {};
      if (currentStatus !== "all") params.status = currentStatus;
      if (currentQ.trim()) params.q = currentQ.trim();
      const res = await api.get("/orders", { params });
      setOrders(res.data);
    } catch (e) {
      toast.error("Erro ao carregar ordens de corte");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchOrders(statusFilter, q), 250);
    return () => clearTimeout(t);
  }, [statusFilter, q, fetchOrders]);

  // --- print ---
  // Independent of the on-screen single-select filter: the operator may
  // want a paper list covering one or several states at once (e.g.
  // "pendente + em produção" to plan the next few days on the floor).
  const [printOpen, setPrintOpen] = useState(false);
  const [printStatuses, setPrintStatuses] = useState(() =>
    Object.fromEntries(ORDER_STATUS_ORDER.map((s) => [s, true]))
  );
  const [printing, setPrinting] = useState(false);
  const [printData, setPrintData] = useState(null);

  useEffect(() => {
    const onAfterPrint = () => setPrintData(null);
    window.addEventListener("afterprint", onAfterPrint);
    return () => window.removeEventListener("afterprint", onAfterPrint);
  }, []);

  const submitPrint = async () => {
    const selected = ORDER_STATUS_ORDER.filter((s) => printStatuses[s]);
    if (selected.length === 0) {
      toast.error("Seleciona pelo menos um estado.");
      return;
    }
    setPrinting(true);
    try {
      const res = await api.get("/orders");
      const filtered = res.data.filter((o) => selected.includes(o.status));
      setPrintData(filtered);
      setPrintOpen(false);
      requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    } catch (e) {
      toast.error("Erro ao preparar impressão");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            title="Voltar ao visualizador"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 bg-foreground flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none">
            <h1 className="font-head text-sm font-bold tracking-tight">ORDENS DE CORTE · {user?.company_name}</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">Gestão de ordens de corte</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/producao")}
            title="Painel de produção"
            className="h-9 px-3 flex items-center gap-2 border border-border text-sm font-medium rounded-[4px] hover:bg-accent transition-colors"
            data-testid="production-link"
          >
            <ListChecks className="w-4 h-4" /> <span className="hidden sm:inline">Produção</span>
          </button>
          <span className="text-xs text-muted-foreground mono hidden sm:inline">{user?.name}</span>
          <button
            data-testid="logout-btn"
            onClick={logout}
            title="Terminar sessão"
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-6 max-w-5xl mx-auto print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Pesquisar cliente ou referência..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 w-64"
                data-testid="orders-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="orders-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estados</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="in_production">Em produção</SelectItem>
                <SelectItem value="done">Concluída</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-[4px]" onClick={() => setPrintOpen(true)} data-testid="print-orders-btn">
              <Printer className="w-4 h-4" /> Imprimir
            </Button>
            <Button className="rounded-[4px]" onClick={() => navigate("/ordens/nova")} data-testid="new-order-btn">
              <Plus className="w-4 h-4" /> Nova ordem
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Referência</TableHead>
                <TableHead>Prazo</TableHead>
                <TableHead className="text-right">Tecidos</TableHead>
                <TableHead className="text-right">Total peças</TableHead>
                <TableHead className="text-right">Progresso</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin inline" /> A carregar...
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhuma ordem encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/ordens/${o.id}`)}
                    data-testid={`order-row-${o.id}`}
                  >
                    <TableCell className="font-medium">{o.client}</TableCell>
                    <TableCell className="mono text-xs">{o.reference}</TableCell>
                    <TableCell className="mono text-xs">{fmtDate(o.due_date)}</TableCell>
                    <TableCell className="text-right mono">{o.fabrics.length}</TableCell>
                    <TableCell className="text-right mono">{o.total_pieces}</TableCell>
                    <TableCell className="text-right mono">{fmtPct(o.progress_pct)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ORDER_STATUS_META[o.status]?.className}>
                        {ORDER_STATUS_META[o.status]?.label || o.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Imprimir ordens de corte</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Estados a incluir</Label>
              {ORDER_STATUS_ORDER.map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <Checkbox
                    id={`print-status-${s}`}
                    checked={printStatuses[s]}
                    onCheckedChange={(v) => setPrintStatuses({ ...printStatuses, [s]: !!v })}
                    data-testid={`print-status-${s}`}
                  />
                  <Label htmlFor={`print-status-${s}`} className="font-normal cursor-pointer">
                    {ORDER_STATUS_META[s]?.label}
                  </Label>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={submitPrint} disabled={printing} className="rounded-[4px]" data-testid="print-confirm-btn">
                {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Imprimir
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {printData && (
        <div className="hidden print:block">
          <style>{"@page { margin: 14mm; }"}</style>
          {printData.length === 0 ? (
            <div className="p-6">
              <h1 className="text-lg font-bold">LOMARTEX · Ordens de Corte — {user?.company_name}</h1>
              <p className="text-sm mt-4">Nenhuma ordem para os estados selecionados.</p>
            </div>
          ) : (
            printData.map((o, idx) => (
              <div key={o.id} className="p-6" style={{ breakBefore: idx > 0 ? "page" : "auto" }}>
                <div className="flex items-start justify-between border-b-2 border-black pb-2 mb-3">
                  <div>
                    <h1 className="text-xl font-bold">Ordem de Corte</h1>
                    <div className="text-base">{o.client} · {o.reference}</div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="font-medium">{user?.company_name}</div>
                    <div>Prazo: {fmtDate(o.due_date)}</div>
                    <div>Estado: {ORDER_STATUS_META[o.status]?.label || o.status}</div>
                    <div>Gerado em {new Date().toLocaleDateString("pt-PT")}</div>
                  </div>
                </div>

                {o.fabrics.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem tecidos associados a esta ordem.</p>
                ) : (
                  o.fabrics.map((f) => (
                    <div key={f.id} className="mb-4" style={{ breakInside: "avoid" }}>
                      <div className="flex items-baseline justify-between mb-1">
                        <h2 className="text-sm font-bold uppercase tracking-wide">{f.name}</h2>
                        <div className="text-xs">
                          {f.file ? f.file.filename : "sem ficheiro"} · {f.layers} camadas · {f.total_pieces} peças
                        </div>
                      </div>
                      {f.matrix.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Matriz por definir.</p>
                      ) : (
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-black">
                              <th className="text-left py-1">Modelo</th>
                              <th className="text-left py-1">Tamanho</th>
                              <th className="text-right py-1">Qtd/camada</th>
                              <th className="text-right py-1">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {f.matrix.map((r, i) => (
                              <tr key={i} className="border-b border-border">
                                <td className="py-0.5">{r.piece_name}</td>
                                <td className="py-0.5">{r.size}</td>
                                <td className="py-0.5 text-right">{r.qty_per_layer}</td>
                                <td className="py-0.5 text-right">{r.qty_per_layer * f.layers}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))
                )}

                <div className="mt-4 pt-2 border-t border-black text-xs flex justify-between">
                  <span>Total peças: {o.total_pieces}</span>
                  <span>Cortadas: {o.cut_pieces}</span>
                  <span>Progresso: {fmtPct(o.progress_pct)}</span>
                  <span>Defeitos: {o.defects_count}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
